import AppLogo from '@/components/layout/app-logo';
import Icon from '@/icons/icon';
import { Link, usePage } from '@inertiajs/react';

interface PageProps {
    auth?: {
        customer?: {
            id: number;
            name: string;
            email: string;
        };
    };
    [key: string]: unknown;
}

export default function Footer() {
    const { auth } = usePage<PageProps>().props;
    const isAuthenticated =
        auth?.customer !== undefined && auth?.customer !== null;

    return (
        <footer className="bg-[#003580] pt-10 text-gray-300">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                {/* Logo */}
                <div className="mb-8 flex items-center">
                    <AppLogo light />
                </div>

                {/* Main content */}
                <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
                    {/* About */}
                    <div>
                        <h3 className="mb-4 text-xl font-semibold text-white">
                            About us
                        </h3>
                        <p className="text-sm text-gray-400">
                            Discover and book the best motels with ease. Enjoy
                            seamless reservations, exclusive deals, and
                            exceptional customer service for a comfortable stay
                            every time.
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h3 className="mb-4 text-xl font-semibold text-white">
                            Quick links
                        </h3>
                        <ul className="space-y-2">
                            <li>
                                <a
                                    href="/my-account"
                                    className="transition hover:text-white"
                                >
                                    My account
                                </a>
                            </li>
                            <li>
                                <Link
                                    href={
                                        isAuthenticated
                                            ? '/bookings'
                                            : '/guest-history'
                                    }
                                    className="transition hover:text-white"
                                >
                                    My bookings
                                </Link>
                            </li>
                            <li>
                                <a
                                    href="/customer-support"
                                    className="transition hover:text-white"
                                >
                                    Contact
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/blog-list"
                                    className="transition hover:text-white"
                                >
                                    Blogs
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Support */}
                    <div>
                        <h3 className="mb-4 text-xl font-semibold text-white">
                            Support
                        </h3>
                        <ul className="space-y-2">
                            <li>
                                <Link
                                    href="/faq"
                                    className="transition hover:text-white"
                                >
                                    FAQs
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/booking-policy"
                                    className="transition hover:text-white"
                                >
                                    Booking Policy
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/privacy-policy"
                                    className="transition hover:text-white"
                                >
                                    Privacy Policy
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/terms-conditions"
                                    className="transition hover:text-white"
                                >
                                    Terms & Conditions
                                </Link>
                            </li>
                            <li>
                                <Link
                                    href="/docs/ota"
                                    className="transition hover:text-white"
                                >
                                    OTA docs
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h3 className="mb-4 text-xl font-semibold text-white">
                            Contact
                        </h3>
                        <div className="space-y-2">
                            <p className="flex items-start gap-2 text-sm text-gray-400">
                                <Icon
                                    iconName="Location"
                                    className="mt-[3px] h-4 w-4 shrink-0 fill-white"
                                />
                                <span>
                                    38099 Schoolcraft Rd Suite 175, Livonia, MI
                                    48150
                                </span>
                            </p>
                            <p className="flex items-center gap-2 text-sm text-gray-400">
                                <Icon
                                    iconName="Mail"
                                    className="h-4 w-4 shrink-0 stroke-white"
                                />
                                <a href={`mailto:support@staydeed.com`}>
                                    support@staydeed.com
                                </a>
                            </p>
                            <p className="flex items-center gap-2 text-sm text-gray-400">
                                <Icon
                                    iconName="Phone"
                                    className="h-4 w-4 shrink-0 fill-white"
                                />
                                <a href={`tel:+17346669878`}>
                                    +1 (734) 666-9878
                                </a>
                            </p>
                        </div>

                        {/* Social */}
                        <div className="mt-4 flex space-x-4">
                            {/* Facebook */}
                            <a
                                href="https://facebook.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="transition hover:text-white"
                            >
                                <Icon
                                    iconName="Facebook"
                                    className="h-5 w-5 fill-white"
                                />
                            </a>

                            {/* Twitter*/}
                            <a
                                href="https://twitter.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="transition hover:text-white"
                            >
                                <Icon
                                    iconName="Twitter"
                                    className="h-5 w-5 fill-white"
                                />
                            </a>

                            {/* Instagram */}
                            <a
                                href="https://instagram.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="transition hover:text-white"
                            >
                                <Icon
                                    iconName="Instagram"
                                    className="h-5 w-5 fill-white"
                                />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="mt-6 flex flex-col items-center justify-between border-t border-white/20 pt-2 sm:flex-row">
                    <p className="text-left text-sm text-gray-400">
                        &copy; {new Date().getFullYear()} Staydeed. All rights
                        reserved.
                    </p>

                    {/* Payment Methods */}
                    <div className="mt-4 flex space-x-3 sm:mt-0">
                        <img
                            src="/assets/Footercard/amex.png"
                            alt="American Express"
                            className="h-6 w-auto"
                        />
                        <img
                            src="/assets/Footercard/visa.png"
                            alt="Visa"
                            className="h-6 w-auto"
                        />
                        <img
                            src="/assets/Footercard/mastercard.png"
                            alt="MasterCard"
                            className="h-6 w-auto"
                        />
                        <img
                            src="/assets/Footercard/discover.png"
                            alt="Discover"
                            className="h-6 w-auto"
                        />
                        <img
                            src="/assets/Footercard/diners.png"
                            alt="Diners Club"
                            className="h-6 w-auto"
                        />
                        <img
                            src="/assets/Footercard/unionpay.png"
                            alt="UnionPay"
                            className="h-6 w-auto"
                        />
                    </div>
                </div>
            </div>
        </footer>
    );
}
